import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import {
  FormProvider,
  type FieldPath,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientText } from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import type { TGuaranteesBaseRelationsValues } from '~/store/guarantees/types.ts';
import {
  BASE_RELATIONS_TEXT,
  PRINCIPAL_OBLIGATION_DETAILS_OPTIONS,
} from '../../../rootSection/constants.ts';
import {
  GuaranteesBaseRelationsFieldName,
  type IBaseRelationsSectionProps,
} from '../../../rootSection/types.ts';
import {
  buildGuaranteesBaseRelationsPayload,
  getGuaranteesBaseRelationsInitialValues,
  guaranteesBaseRelationsSectionResolver,
} from '../../../utils/sections/utils.ts';
import FlowSection from '../shared/FlowSection.tsx';
import { utils } from '../shared/utils.ts';
import { useGuaranteesRootApplicationSectionContext } from '../RootApplicationSectionContext.tsx';

const BaseRelationsSection: React.FC<IBaseRelationsSectionProps> = ({
  baseRelations,
  isLoading,
  isSaving,
  isProzorroTender,
  onSubmit,
}) => {
  const {
    isReadOnly,
    requireChangesToEnableActions,
    registerDependentSectionValidation,
  } = useGuaranteesRootApplicationSectionContext();
  const defaultValues = React.useMemo(
    () => getGuaranteesBaseRelationsInitialValues(baseRelations, isProzorroTender),
    [baseRelations, isProzorroTender]
  );
  const formMethods = useForm<TGuaranteesBaseRelationsValues>({
    defaultValues,
    resolver: guaranteesBaseRelationsSectionResolver,
    mode: 'onSubmit',
  });
  const {
    getValues,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { isDirty },
  } = formMethods;
  const isFormDisabled = isSaving || isReadOnly;
  const isRelationsDocumentDisabled = isFormDisabled || isProzorroTender;
  const { isActionDisabled } = utils({
    defaultValues,
    isDirty,
    isDisabled: isFormDisabled,
    requireChangesToEnableActions,
    reset,
  });

  const handleBaseRelationsSubmit: SubmitHandler<TGuaranteesBaseRelationsValues> = async (
    values
  ) => {
    const isSaved = await onSubmit(buildGuaranteesBaseRelationsPayload(values));

    if (isSaved) {
      reset(values);
    }
  };

  const touchAndValidateFields = React.useCallback(
    (fieldNames: readonly FieldPath<TGuaranteesBaseRelationsValues>[]) => {
      fieldNames.forEach((fieldName) => {
        setValue(fieldName, getValues(fieldName), {
          shouldDirty: false,
          shouldTouch: true,
          shouldValidate: false,
        });
      });

      void trigger(fieldNames);
    },
    [getValues, setValue, trigger]
  );

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      touchAndValidateFields([GuaranteesBaseRelationsFieldName.PrincipalObligationDetails]);
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [defaultValues, touchAndValidateFields]);

  React.useEffect(
    () => registerDependentSectionValidation('baseRelations', () => trigger(undefined, { shouldFocus: true })),
    [registerDependentSectionValidation, trigger]
  );

  return (
    <FlowSection
      step={BASE_RELATIONS_TEXT.sectionStepLabel}
      title={BASE_RELATIONS_TEXT.sectionTitle}
      description={BASE_RELATIONS_TEXT.sectionDescription}
    >
      {isLoading ? (
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          <Typography sx={guaranteesClientPageStyles.flowLockHint}>
            {BASE_RELATIONS_TEXT.loadingText}
          </Typography>
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <Box
            component="form"
            onSubmit={(event) => {
              void handleSubmit(handleBaseRelationsSubmit)(event);
            }}
          >
            <Box sx={guaranteesClientPageStyles.flowFieldsGrid}>
              <FormInput
                name={GuaranteesBaseRelationsFieldName.RelationsDocument}
                label={BASE_RELATIONS_TEXT.relationsDocumentLabel}
                disabled={isRelationsDocumentDisabled}
                sx={guaranteesClientPageStyles.flowField}
              />
              <FormAutocomplete
                name={GuaranteesBaseRelationsFieldName.PrincipalObligationDetails}
                label={
                  <Box component="span" sx={guaranteesClientPageStyles.flowInlineTooltipLabel}>
                    {BASE_RELATIONS_TEXT.principalObligationDetailsLabel}
                    <InfoTooltip
                      title={guaranteesClientText.principalTooltip}
                      iconStyles={guaranteesClientPageStyles.flowInlineTooltipIcon}
                    />
                  </Box>
                }
                placeholder={BASE_RELATIONS_TEXT.principalObligationDetailsPlaceholder}
                options={PRINCIPAL_OBLIGATION_DETAILS_OPTIONS}
                disabled={isFormDisabled}
                sx={guaranteesClientPageStyles.flowLongAutocompleteField}
              />
            </Box>

            <Box sx={guaranteesClientPageStyles.flowActions}>
              <Button
                type="submit"
                variant="contained"
                disabled={isActionDisabled}
                startIcon={isSaving ? <ButtonLoadingIcon /> : undefined}
                sx={dataFormStyles.documentPrimaryAction}
              >
                {BASE_RELATIONS_TEXT.saveButton}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      )}
    </FlowSection>
  );
};

export default BaseRelationsSection;
